import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { getBackendUrl } from "../utils/env";

let showSessionExpiredModal = null;
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const https = axios.create({
  baseURL: getBackendUrl(),
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - Thêm token vào header
https.interceptors.request.use(
  (config) => {
    const token = Cookies.get("token");

    // CHỈ thêm token nếu có và không phải request refresh token
    if (token && !config.url?.includes("/refresh/")) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      config.headers["Content-Type"] = "multipart/form-data";
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor với refresh token logic
https.interceptors.response.use(
  (response) => {
    if (response && response.data) return response.data;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // CHỈ xử lý refresh token khi:
    // 1. Có response
    // 2. Status là 403
    // 3. Message là "Invalid or expired token"
    // 4. Không phải request refresh token
    // 5. Chưa retry
    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data?.message === "Invalid or expired token" &&
      !originalRequest.url?.includes("/refresh/") &&
      !originalRequest._retry
    ) {
      console.log("Token expired, attempting refresh...");

      if (isRefreshing) {
        // Nếu đang refresh, thêm request vào queue
        console.log("Already refreshing, adding to queue");
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return https(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = Cookies.get("refreshToken");

      if (!refreshToken) {
        console.error("No refresh token available");
        isRefreshing = false;

        // CHỈ xóa cookies và redirect khi thực sự không có refreshToken
        Cookies.remove("token");
        Cookies.remove("refreshToken");
        Cookies.remove("user");

        if (typeof showSessionExpiredModal === "function") {
          showSessionExpiredModal();
        }

        window.location.href = "/login";
        return Promise.reject(error);
      }

      try {
        console.log("Calling refresh token API...");

        // Gọi API refresh token - KHÔNG dùng https instance để tránh loop
        const response = await axios.post(
          `${getBackendUrl()}/refresh/cms`,
          { refreshToken: refreshToken },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        console.log("Refresh token successful");

        const newToken = response.data?.token;
        const newRefreshToken = response.data?.refreshToken;

        if (!newToken) {
          throw new Error("No token received from refresh");
        }

        // Lưu token mới vào cookie
        Cookies.set("token", newToken, { expires: 7 });
        if (newRefreshToken) {
          Cookies.set("refreshToken", newRefreshToken, { expires: 30 });
        }

        // Dispatch event để AuthContext update
        window.dispatchEvent(
          new CustomEvent("tokenRefreshed", {
            detail: {
              token: newToken,
              refreshToken: newRefreshToken || refreshToken,
            },
          }),
        );

        // Process queue với token mới
        processQueue(null, newToken);

        // Retry request gốc với token mới
        originalRequest.headers["Authorization"] = "Bearer " + newToken;

        isRefreshing = false;
        return https(originalRequest);
      } catch (refreshError) {
        console.error("Refresh token failed:", refreshError);

        // Refresh token thất bại, logout
        processQueue(refreshError, null);
        isRefreshing = false;

        // CHỈ logout khi refresh token thực sự thất bại (401, 403)
        if (
          refreshError.response?.status === 401 ||
          refreshError.response?.status === 403
        ) {
          Cookies.remove("token");
          Cookies.remove("refreshToken");
          Cookies.remove("user");

          if (typeof showSessionExpiredModal === "function") {
            showSessionExpiredModal();
          }

          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      }
    }


    // Xử lý các lỗi xác thực (401) nhưng không phải do hết hạn token (hoặc refresh fail)
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      console.warn("Unauthorized, clearing session...");
      toast.error("Tài khoản của bạn đã bị hết hạn hoặc không có quyền truy cập!");
      Cookies.remove("token");
      Cookies.remove("refreshToken");
      Cookies.remove("user");

      if (typeof showSessionExpiredModal === "function") {
        showSessionExpiredModal();
      } else {
        window.location.href = "/login";
      }
    }

    // Các lỗi khác - KHÔNG logout
    return Promise.reject(error);
  },
);

export const setSessionExpiredHandler = (handler) => {
  showSessionExpiredModal = handler;
};

export default https;
